import { useEffect, useRef, useState } from "react";
import { formatTrialCountdown } from "Utils/trialStatus";
import styles from "./TrialStatus.module.scss";

const MINUTE_MS = 60 * 1000;

const formatEndTime = (date) =>
  date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * "Your trial ends in 4h 12m", updated each time the minute changes. The
 * changing text is hidden from screen readers (it sits in a live region);
 * they get the fixed end time instead.
 */
const TrialCountdown = ({ endsAt, onComplete }) => {
  const [now, setNow] = useState(() => Date.now());
  const onCompleteRef = useRef(onComplete);
  const endTime = endsAt?.getTime?.() ?? 0;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!endTime) return undefined;
    let timer;
    const scheduleNextTick = () => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        onCompleteRef.current?.();
        return;
      }
      // Wake just after the displayed minute changes (or just after the end).
      timer = window.setTimeout(() => {
        setNow(Date.now());
        scheduleNextTick();
      }, (remaining % MINUTE_MS) + 1);
    };
    scheduleNextTick();
    return () => window.clearTimeout(timer);
  }, [endTime]);

  if (!endTime) return null;

  return (
    <>
      <span aria-hidden="true">
        Your trial ends in{" "}
        <span className={styles.countdown}>
          {formatTrialCountdown(endTime - now)}
        </span>
      </span>
      <span className={styles.srOnly}>
        Your trial ends today at {formatEndTime(endsAt)}
      </span>
    </>
  );
};

export default TrialCountdown;
